import { useEffect } from "react";
import { confirmDialog } from "../utils/dialogs";

// Shared modal-stack counter so nested modals know whether they are
// the top-most open modal. Each call to `useEscClose` pushes itself on
// mount/activation and pops on cleanup; only the TOP of the stack
// reacts to a given Escape press. Without this, a nested modal's Esc
// handler closes both its own layer AND its parent in one keypress
// (stopPropagation on `window` doesn't fence other `window` listeners).
const _modalStack = [];
let _nextId = 1;

/**
 * Close a modal when Escape is pressed.
 *
 * If `opts.dirty` is truthy, the user is warned before closing — this
 * covers the 2026-02-17 project-wide requirement that "Esc should
 * abandon with a warning on all modals". Modals that don't track
 * dirty state get the old behaviour (immediate close) by default —
 * they simply pass no options.
 *
 * @param {boolean} active
 * @param {() => void} onClose
 * @param {{ dirty?: boolean, confirmMessage?: string }} [opts]
 */
export default function useEscClose(active, onClose, opts = {}) {
  useEffect(() => {
    if (!active || typeof onClose !== "function") return;
    const myId = _nextId++;
    _modalStack.push(myId);
    const handler = (e) => {
      if (e.key !== "Escape" && e.key !== "Esc") return;
      // Only the top-most modal on the stack reacts. This fences
      // parent modals from closing when a nested child handles Esc.
      if (_modalStack[_modalStack.length - 1] !== myId) return;
      // stopImmediatePropagation also prevents other window listeners
      // registered at the same time from firing on this event.
      e.stopImmediatePropagation();
      e.stopPropagation();
      if (opts.dirty) {
        const msg = opts.confirmMessage ||
          "Discard the changes you just made?";
        confirmDialog(msg, { confirmLabel: "Discard", danger: true }).then((ok) => {
          if (ok) onClose();
        });
        return;
      }
      onClose();
    };
    // `capture: true` ensures our stack-aware handler runs before any
    // listener registered later with default (bubble) phase.
    window.addEventListener("keydown", handler, true);
    return () => {
      window.removeEventListener("keydown", handler, true);
      const i = _modalStack.lastIndexOf(myId);
      if (i >= 0) _modalStack.splice(i, 1);
    };
  }, [active, onClose, opts.dirty, opts.confirmMessage]);
}
